import type { DriverStanding, SeasonState, Team } from "../../types";
import { bind, bindActivate } from "../dom";
import { esc } from "../helpers";
import { openDailyShareModal, openShareModal } from "../modals";
import { buildDailyShareText, buildResultImage } from "../share";
import { t } from "../i18n";
import type { Screen, ScreenContext } from "./context";

// Fill the #tabBody standings table for the active result tab.
function renderResultTab(
  tab: "wcc" | "wdc",
  constructors: readonly Team[],
  drivers: readonly DriverStanding[],
): void {
  const body = document.getElementById("tabBody");
  if (!body) return;
  const T = t();
  if (tab === "wcc") {
    body.innerHTML = `<table><tr><th>${T.thPos}</th><th>${T.thConstructor}</th><th class="num">${T.thPts}</th></tr>${constructors
      .map(
        (c, i) =>
          `<tr class="${c.isPlayer ? "you" : ""}"><td class="pos">${i + 1}</td><td>${esc(c.name)}</td><td class="num">${c.points}</td></tr>`,
      )
      .join("")}</table>`;
  } else {
    body.innerHTML = `<table><tr><th>${T.thPos}</th><th>${T.thDriver}</th><th>${T.thConstructor}</th><th class="num">${T.thPts}</th></tr>${drivers
      .map(
        (d, i) =>
          `<tr class="${d.isPlayer ? "you" : ""}"><td class="pos">${i + 1}</td><td>${esc(d.driverName)}</td><td style="color:var(--muted)">${esc(d.teamName)}</td><td class="num">${d.points}</td></tr>`,
      )
      .join("")}</table>`;
  }
}

// Render the result image, then open a popup so the player can preview it and
// choose to share via the device sheet or copy it to the clipboard.
async function shareResult(s: SeasonState): Promise<void> {
  if (!s.standings) return;
  // Daily shares a text scoreboard (no lineup), not the result image.
  if (s.daily) {
    openDailyShareModal(buildDailyShareText(s));
    return;
  }
  const T = t();
  const btn = document.getElementById("shareBtn") as HTMLButtonElement | null;
  const original = btn?.textContent ?? T.shareResult;
  if (btn) {
    btn.disabled = true;
    btn.textContent = T.sharePreparing;
  }
  try {
    const img = await buildResultImage(s);
    if (img) openShareModal(img);
    else if (btn) btn.textContent = T.shareUnavailable;
  } catch {
    if (btn) btn.textContent = T.shareUnavailable;
  } finally {
    if (btn) {
      btn.disabled = false;
      if (btn.textContent === T.sharePreparing) btn.textContent = original;
    }
  }
}

/** The championship result screen: champions, standings tabs, and sharing. */
export function renderResult(ctx: ScreenContext): Screen {
  const s = ctx.state.season!;
  const view = ctx.view;
  const st = s.standings!;
  const wccTeam = st.constructors[0]!;
  const wdc = st.drivers[0]!;
  const won = wccTeam.isPlayer || wdc.isPlayer;
  const T = t();
  const html = `<div class="fade">
      <div class="champbox">
        <div class="crown">${T.wccChampion}</div>
        <div class="who">${esc(wccTeam.name)}${wccTeam.isPlayer ? " ◈" : ""}</div>
        <div class="crown" style="margin-top:10px">${T.wdcChampion}</div>
        <div class="who">${esc(wdc.driverName)}${wdc.isPlayer ? " ◈" : ""}</div>
        ${won ? `<p style="color:var(--green);margin-top:10px;font-weight:700">${T.titleSecured}</p>` : `<p style="color:var(--muted);margin-top:10px">${T.noTitle}</p>`}
        ${won && s.fromMemory ? `<div class="memwin">${T.memoryWin}</div>` : ""}
      </div>
      <div class="tabs" role="group" aria-label="${esc(T.standingsTitle)}">
        <div class="tab ${view.resultTab === "wcc" ? "active" : ""}" id="tabWcc" role="button" tabindex="0" aria-pressed="${view.resultTab === "wcc"}">${T.tabConstructors}</div>
        <div class="tab ${view.resultTab === "wdc" ? "active" : ""}" id="tabWdc" role="button" tabindex="0" aria-pressed="${view.resultTab === "wdc"}">${T.tabDrivers}</div>
      </div>
      <div id="tabBody"></div>
      ${s.daily ? `<div id="dailyLb" class="lbHost"></div>` : ""}
      <div style="margin-top:22px;display:flex;gap:10px;flex-wrap:wrap">
        <button class="btn primary" id="newSeason">${T.newSeason}</button>
        <button class="btn" id="shareBtn">${T.shareResult}</button>
      </div>
    </div>`;
  return {
    html,
    bind() {
      bindActivate("tabWcc", () => {
        view.resultTab = "wcc";
        ctx.rerender();
      });
      bindActivate("tabWdc", () => {
        view.resultTab = "wdc";
        ctx.rerender();
      });
      bind("newSeason", "click", () => ctx.actions.goMenu());
      bind("shareBtn", "click", () => void shareResult(s));
      renderResultTab(view.resultTab, st.constructors, st.drivers);
      if (s.daily) {
        // Make sure a result reached by reload/menu (not just the finish flow) also
        // posts, then paint the panel.
        ctx.daily.ensureSubmitted(s);
        void ctx.daily.renderLeaderboard(s);
      }
    },
  };
}
